<template>
  <div class="home">
    <!-- Hero -->
    <section class="hero">
      <img src="@/assets/emblem-256.png" alt="" class="hero-watermark" aria-hidden="true" />
      <div class="container hero-inner">
        <span class="eyebrow eyebrow-light">{{ $t('home.hero.eyebrow') }}</span>
        <h1>{{ $t('home.hero.title') }}</h1>
        <p class="hero-sub">{{ $t('home.hero.subtitle') }}</p>
        <div class="hero-actions">
          <router-link to="/donate" class="btn btn-accent">{{ $t('nav.donate') }}</router-link>
          <router-link to="/contact" class="btn btn-outline">{{ $t('home.hero.volunteer') }}</router-link>
        </div>
      </div>
    </section>

    <!-- Impact stats -->
    <section class="stats-strip">
      <div class="container grid grid-4">
        <!-- Placeholder figures — replace with your verified, audited numbers -->
        <div class="stat"><div class="stat-number">500+</div><div class="stat-label">{{ $t('home.stats.volunteers') }}</div></div>
        <div class="stat"><div class="stat-number">25+</div><div class="stat-label">{{ $t('home.stats.localities') }}</div></div>
        <div class="stat"><div class="stat-number">1,000+</div><div class="stat-label">{{ $t('home.stats.trees') }}</div></div>
        <div class="stat"><div class="stat-number">4</div><div class="stat-label">{{ $t('home.stats.focus') }}</div></div>
      </div>
    </section>

    <!-- Focus areas -->
    <section class="section">
      <div class="container">
        <div class="section-head">
          <span class="eyebrow">{{ $t('home.pillars.eyebrow') }}</span>
          <h2>{{ $t('home.pillars.title') }}</h2>
          <p>{{ $t('home.pillars.subtitle') }}</p>
        </div>
        <div class="grid grid-4">
          <div class="card pillar-card" v-for="p in pillars" :key="p.key">
            <IconBadge :icon="p.icon" :tone="p.tone" size="lg" />
            <h3>{{ $t(`home.pillars.${p.key}.title`) }}</h3>
            <p>{{ $t(`home.pillars.${p.key}.text`) }}</p>
            <router-link to="/programs" class="card-link">{{ $t('home.pillars.learnMore') }} <IconBadge icon="arrow" size="sm" tone="green" /></router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- About teaser -->
    <section class="section section-alt">
      <div class="container about-teaser">
        <div class="about-copy">
          <span class="eyebrow">{{ $t('home.about.eyebrow') }}</span>
          <h2>{{ $t('home.about.title') }}</h2>
          <p>{{ $t('home.about.text') }}</p>
          <router-link to="/about" class="btn btn-primary">{{ $t('home.about.cta') }}</router-link>
        </div>
        <div class="about-values card">
          <h3>{{ $t('home.about.valuesTitle') }}</h3>
          <ul class="value-list">
            <li><IconBadge icon="check" size="sm" tone="green" /> {{ $t('home.about.v1') }}</li>
            <li><IconBadge icon="check" size="sm" tone="green" /> {{ $t('home.about.v2') }}</li>
            <li><IconBadge icon="check" size="sm" tone="green" /> {{ $t('home.about.v3') }}</li>
            <li><IconBadge icon="check" size="sm" tone="green" /> {{ $t('home.about.v4') }}</li>
          </ul>
        </div>
      </div>
    </section>

    <!-- Get involved -->
    <section class="section">
      <div class="container">
        <div class="section-head">
          <span class="eyebrow">{{ $t('home.involve.eyebrow') }}</span>
          <h2>{{ $t('home.involve.title') }}</h2>
        </div>
        <div class="grid grid-3">
          <div class="card involve-card">
            <IconBadge icon="heart" tone="amber" size="lg" />
            <h3>{{ $t('home.involve.donateTitle') }}</h3>
            <p>{{ $t('home.involve.donateText') }}</p>
            <router-link to="/donate" class="btn btn-accent">{{ $t('nav.donate') }}</router-link>
          </div>
          <div class="card involve-card">
            <IconBadge icon="hand" tone="green" size="lg" />
            <h3>{{ $t('home.involve.volunteerTitle') }}</h3>
            <p>{{ $t('home.involve.volunteerText') }}</p>
            <router-link to="/contact" class="btn btn-primary">{{ $t('home.involve.volunteerBtn') }}</router-link>
          </div>
          <div class="card involve-card">
            <IconBadge icon="users" tone="navy" size="lg" />
            <h3>{{ $t('home.involve.partnerTitle') }}</h3>
            <p>{{ $t('home.involve.partnerText') }}</p>
            <router-link to="/contact" class="btn btn-outline-teal">{{ $t('home.involve.partnerBtn') }}</router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="cta-band">
      <div class="container cta-inner">
        <h2>{{ $t('home.cta.title') }}</h2>
        <p>{{ $t('home.cta.text') }}</p>
        <div class="hero-actions">
          <router-link to="/donate" class="btn btn-accent">{{ $t('nav.donate') }}</router-link>
          <router-link to="/programs" class="btn btn-outline">{{ $t('home.cta.programs') }}</router-link>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import IconBadge from '@/components/IconBadge.vue';

export default {
  name: 'HomePage',
  components: { IconBadge },
  data() {
    return {
      pillars: [
        { key: 'women', icon: 'users', tone: 'rose' },
        { key: 'animals', icon: 'paw', tone: 'amber' },
        { key: 'environment', icon: 'tree', tone: 'green' },
        { key: 'clean', icon: 'droplet', tone: 'navy' },
      ],
    };
  },
};
</script>

<style scoped>
.hero {
  position: relative; overflow: hidden;
  background: linear-gradient(135deg, var(--charcoal-dark), var(--charcoal));
  color: #fff; padding: 96px 0 88px; text-align: center;
}
.hero-watermark {
  position: absolute; right: -60px; top: 50%; transform: translateY(-50%);
  width: 420px; height: 420px; object-fit: contain; opacity: 0.12; pointer-events: none;
}
.hero-inner { position: relative; max-width: 780px; margin: 0 auto; }
.eyebrow-light { color: #8FDDD8; text-transform: uppercase; letter-spacing: 1.5px; font-weight: 700; font-size: 0.8rem; }
.hero h1 { font-size: clamp(1.9rem, 4vw, 2.7rem); margin: 14px 0 18px; line-height: 1.25; }
.hero-sub { font-size: 1.05rem; color: rgba(255,255,255,0.88); max-width: 620px; margin: 0 auto; }
.hero-actions { margin-top: 30px; display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; }

.stats-strip { background: #fff; box-shadow: 0 2px 14px rgba(0,0,0,0.05); position: relative; z-index: 1; }
.stats-strip .grid { padding: 34px 0; text-align: center; }

.pillar-card { text-align: left; display: flex; flex-direction: column; gap: 6px; }
.pillar-card h3 { margin-top: 14px; font-size: 1.08rem; color: var(--charcoal); }
.pillar-card p { color: var(--muted); font-size: 0.92rem; margin: 0; }
.card-link { margin-top: 10px; display: inline-flex; align-items: center; gap: 8px; color: var(--brand-teal); font-weight: 600; text-decoration: none; font-size: 0.9rem; }

.about-teaser { display: grid; grid-template-columns: 1.4fr 1fr; gap: 40px; align-items: center; }
.about-copy p { color: var(--muted); margin: 16px 0 26px; }
.value-list { list-style: none; padding: 0; margin: 16px 0 0; display: flex; flex-direction: column; gap: 14px; font-size: 0.94rem; }
.value-list li { display: flex; align-items: center; gap: 10px; }
@media (max-width: 860px) { .about-teaser { grid-template-columns: 1fr; } }

.involve-card { text-align: center; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.involve-card h3 { color: var(--charcoal); }
.involve-card p { color: var(--muted); font-size: 0.92rem; margin: 0 0 8px; }

.cta-band { background: var(--charcoal); color: #fff; text-align: center; padding: 64px 0; }
.cta-inner h2 { font-size: clamp(1.5rem, 3vw, 2rem); }
.cta-inner p { margin: 12px 0 0; color: rgba(255,255,255,0.92); }
.cta-band .btn-outline { border-color: rgba(255,255,255,0.8); }
</style>
